"use client";

// Utility that detects when devtools are opened and redirects to about:blank.
// Destroys history to prevent users from navigating back.
export type DevtoolsGuardOptions = {
  message?: string;
  allowedOrigins?: string[]; // Array of allowed origins (e.g., ['http://localhost:3000', 'https://test.domain.com'])
  blockOnMobileAndTablet?: boolean; // Set true to keep guard active on phones/tablets
};

type Cleanup = () => void;

const CONSOLE_PRIME_INTERVAL = 4000;
const DEBUGGER_PROBE_INTERVAL = 1500;
const DEBUGGER_RESPONSE_TIMEOUT = 900;
const LOCAL_HOSTNAMES = new Set(["localhost", "127.0.0.1", "::1"]);
const MOBILE_OR_TABLET_UA_REGEX =
  /android|iphone|ipad|ipod|mobile|tablet|silk|kindle|blackberry|iemobile|opera mini/i;

type NavigatorWithUAData = Navigator & {
  userAgentData?: {
    mobile?: boolean;
  };
};

const isMobileOrTabletBrowser = () => {
  if (typeof navigator === "undefined") {
    return false;
  }

  const nav = navigator as NavigatorWithUAData;
  if (nav.userAgentData?.mobile === true) {
    return true;
  }

  const ua = navigator.userAgent ?? "";
  if (MOBILE_OR_TABLET_UA_REGEX.test(ua)) {
    return true;
  }

  // iPadOS may report as Macintosh while still being a touch-only tablet.
  if (/macintosh/i.test(ua) && navigator.maxTouchPoints > 1) {
    return true;
  }

  return false;
};

const createWorkerProbe = () => {
  if (typeof Worker === "undefined" || typeof URL === "undefined" || typeof Blob === "undefined") {
    return null;
  }

  const workerSource = `self.onmessage = function () {
    try {
      debugger;
    } catch (err) {}
    self.postMessage({ type: 'probe', ts: Date.now() });
  };`;

  try {
    const blob = new Blob([workerSource], { type: "application/javascript" });
    const workerUrl = URL.createObjectURL(blob);
    const worker = new Worker(workerUrl);
    return { worker, workerUrl };
  } catch {
    return null;
  }
};

const buildWildcardRegex = (pattern: string) => {
  const escaped = pattern.replace(/[.+?^${}()|[\\]\\]/g, "\\$&");
  const wildcarded = escaped.replace(/\\\*/g, ".*");
  return new RegExp(`^${wildcarded}$`);
};

export function installDevtoolsGuard(options?: DevtoolsGuardOptions) {
  if (typeof window === "undefined") return () => {};

  if (!options?.blockOnMobileAndTablet && isMobileOrTabletBrowser()) {
    return () => {};
  }

  const allowedOrigins = options?.allowedOrigins ?? [];

  const isOriginAllowed = () => {
    const currentOrigin = window.location.origin;
    const currentHostname = window.location.hostname;

    if (LOCAL_HOSTNAMES.has(currentHostname.toLowerCase())) {
      return true;
    }

    return allowedOrigins.some((allowed) => {
      const normalized = allowed.endsWith("/") ? allowed.slice(0, -1) : allowed;
      if (!normalized) return false;

      if (normalized === currentOrigin || normalized === currentHostname) {
        return true;
      }

      if (normalized.includes("*")) {
        const regex = buildWildcardRegex(normalized);
        return regex.test(currentOrigin) || regex.test(currentHostname);
      }

      return false;
    });
  };

  if (isOriginAllowed()) {
    return () => {};
  }

  let redirected = false;
  const cleanups: Cleanup[] = [];
  const registerCleanup = (fn: Cleanup) => cleanups.push(fn);

  const redirectToBlank = () => {
    if (redirected) return;
    redirected = true;

    if (options?.message) {
      try {
        sessionStorage.setItem("devtools_guard_notice", options.message);
      } catch {}
    }

    try {
      window.history.replaceState({}, "", "about:blank");
    } catch {}

    window.location.href = "about:blank";
  };

  const primeConsoleTrap = () => {
    const bait = new Image();

    Object.defineProperty(bait, "id", {
      get() {
        redirectToBlank();
        return "";
      },
    });

    bait.toString = () => {
      redirectToBlank();
      return "";
    };

    console.log(bait);
  };

  primeConsoleTrap();
  const consoleInterval = window.setInterval(primeConsoleTrap, CONSOLE_PRIME_INTERVAL);
  registerCleanup(() => window.clearInterval(consoleInterval));

  const handleVisibilityChange = () => {
    if (document.visibilityState === "visible") {
      primeConsoleTrap();
    }
  };

  document.addEventListener("visibilitychange", handleVisibilityChange, true);
  registerCleanup(() => document.removeEventListener("visibilitychange", handleVisibilityChange, true));

  const workerProbe = createWorkerProbe();
  if (workerProbe) {
    const { worker, workerUrl } = workerProbe;
    let pendingTimeout: number | null = null;

    const clearPending = () => {
      if (pendingTimeout) {
        window.clearTimeout(pendingTimeout);
        pendingTimeout = null;
      }
    };

    const pokeWorker = () => {
      if (redirected) return;
      if (pendingTimeout) return;

      pendingTimeout = window.setTimeout(() => {
        pendingTimeout = null;
        redirectToBlank();
      }, DEBUGGER_RESPONSE_TIMEOUT);

      try {
        worker.postMessage({ type: "probe" });
      } catch {
        clearPending();
      }
    };

    worker.addEventListener(
      "message",
      () => {
        clearPending();
      },
      true,
    );

    pokeWorker();
    const workerInterval = window.setInterval(pokeWorker, DEBUGGER_PROBE_INTERVAL);

    registerCleanup(() => {
      clearPending();
      window.clearInterval(workerInterval);
      try {
        worker.terminate();
      } catch {}
      try {
        URL.revokeObjectURL(workerUrl);
      } catch {}
    });
  }

  const keyboardHandler = (event: KeyboardEvent) => {
    const key = event.key?.toLowerCase();
    const ctrlOrCmd = event.ctrlKey || event.metaKey;
    const shift = event.shiftKey;
    const alt = event.altKey;

    const shortcutTriggered =
      key === "f12" ||
      (ctrlOrCmd && shift && key && ["i", "j", "c", "k"].includes(key)) ||
      (event.metaKey && alt && key && ["i", "j"].includes(key));

    if (shortcutTriggered) {
      event.preventDefault();
      redirectToBlank();
    }
  };

  window.addEventListener("keydown", keyboardHandler, true);
  registerCleanup(() => window.removeEventListener("keydown", keyboardHandler, true));

  return () => {
    cleanups.forEach((cleanup) => {
      try {
        cleanup();
      } catch {}
    });
  };
}
